import { Capacitor } from '@capacitor/core';
import { isInArray, tryAsEnum } from 'app/tools/general.types';
import _ from 'lodash';
import { Except, ValueOf } from 'type-fest';

const nodeEnvOptions = ['production', 'development', 'test'] as const;

export enum AppVariant {
  CONSUMER = 'consumer',
  BUSINESS = 'business',
}

export enum AppPlatform {
  WEB = 'web',
  IOS = 'ios',
  ANDROID = 'android',
}

export const getAppPlatform = (): `${AppPlatform}` =>
  tryAsEnum(Capacitor.getPlatform(), AppPlatform);

// NOTE: this class represents the options each env value can take.
// An empty string ('') means that the value can be any string.
class AppEnvOptions {
  static APP_VARIANT = _.values(AppVariant);
  static API_ROOT = '';
  static FETCHIFY_KEY = '';
  static GOOGLE_API_KEY = '';
  static RELEASE_ID = '';
  static SENTRY_DSN = '';
  static SENTRY_ENV = '';
}

type AppEnvOptionsType = Except<typeof AppEnvOptions, 'prototype'>;

export type AppEnv = {
  // NOTE: this works with both the array type and the string because
  // for types, string[number] === string
  [K in keyof AppEnvOptionsType]: AppEnvOptionsType[K][number];
};

export const NODE_ENV = (foundEnv => {
  if (!isInArray(foundEnv, nodeEnvOptions)) {
    throw new Error(
      `NODE_ENV must be set to one of: ${nodeEnvOptions.join(', ')}`
    );
  }
  return foundEnv;
})(process.env.NODE_ENV);

export const appEnv = _(AppEnvOptions)
  .entries()
  .map(
    ([envName, envValueOptions]: [
      keyof AppEnv,
      ValueOf<typeof AppEnvOptions>
    ]) => {
      if (envValueOptions instanceof AppEnvOptions) {
        throw new Error('this should never occur');
      }
      if (NODE_ENV === 'test') {
        // NOTE: in testing, appEnv doesn't matter to fill with this instead
        return ['default', undefined];
      }
      if (!_.isObject(window.env)) {
        throw new Error('window.env missing or not set correctly');
      }

      let envValue = window.env[envName] || '';

      if (_.isArray(envValueOptions)) {
        if (!_.includes(envValueOptions, envValue)) {
          throw new Error(
            `${envName} is incorrect or missing from window.env. ` +
              `Value of ${envName} should be one from ${JSON.stringify(
                envValueOptions
              )}, but found ${JSON.stringify(envValue)} instead.`
          );
        }
      } else if (typeof envValueOptions !== typeof envValue) {
        throw new Error(
          `${envName} is incorrect or missing from window.env. Value ` +
            `of ${envName} should be of type ${typeof envValueOptions}, ` +
            `but found type ${typeof envValue} instead.`
        );
      }

      if (envName === 'APP_VARIANT') {
        envValue = process.env.REACT_APP_VARIANT_OVERRIDE || envValue;
      }

      return [envName, envValue];
    }
  )
  .fromPairs()
  .value() as AppEnv;

export const MIRROR_CODE = process.env.REACT_APP_MIRROR_CODE;
